import type { PersonInvolvementKind } from "../types/involvement.js";
import type { CustomerRole } from "../types/thalamos.js";

/**
 * The involvement kinds a role makes a user eligible to hold.
 *
 * The counterpart of `personActionsConveyedByInvolvementKind`: that says what
 * an involvement conveys, this says who may hold one. Not everybody at a
 * residential unit should be eligible to be the responsible user, who decides
 * who can see a person, and eligibility is standing conferred by an
 * organisation, which is what a role already is.
 *
 * The organisational kinds are absent from every row: a duty of care or a
 * placement is held by an organisation, not by a user with a role.
 */
export const eligibleInvolvementKindsByRole: Record<
  CustomerRole | "none",
  readonly PersonInvolvementKind[]
> = {
  // Eligible to be the responsible user as well, because whoever creates a
  // person's record allocates the first responsible user and may have nobody
  // but themselves to allocate.
  organisation_admin: ["responsible_user", "viewer"],
  responsible_user: ["responsible_user", "approver", "viewer"],
  assessor: ["designated_assessor", "assessor", "viewer"],
  approver: ["approver", "viewer"],
  viewer: ["viewer"],
  // A guest contributes an assessment or reads the ones shared with them, the
  // only routes by which somebody outside every organisation reaches a person.
  // Staff read through involvements of their own, so no other role is
  // eligible for `shared_assessment_reader`.
  none: ["assessor", "shared_assessment_reader"],
};
